import usePopups, { type SARLogPopups } from "@/stores/popupsStore"
import { Alert, formatFileSize } from "vue-utils"

export interface UploadValidationOptions {
	required?: boolean
	maxUploadSize?: number
	allowedMimeTypes?: string[]
}

export function useUploadValidation() {
	const popups = usePopups()

	function validateUpload(files: File[], options: UploadValidationOptions): boolean {
		return UploadValidation.validateFileUpload(popups, files, options)
	}

	return { validateUpload }
}

class UploadValidation {
	private constructor(
		private readonly popups: SARLogPopups,
		private readonly files: File[],
		private readonly options: UploadValidationOptions
	) {}

	static validateFileUpload(popups: SARLogPopups, files: File[], options: UploadValidationOptions): boolean {
		const validation = new UploadValidation(popups, files, options)
		return validation.validateFileUpload()
	}

	private validateFileUpload(): boolean {
		return this.validateFileCount() && this.validateFileSize() && this.validateMimeTypes()
	}

	private validateFileCount(): boolean {
		if (this.files.length > 0 || !this.options.required) {
			return true
		}
		void this.popups.showAlertPopup(() => (
			<Alert title="Please Upload a File">Please select at least one file to upload</Alert>
		))
		return false
	}

	private validateFileSize(): boolean {
		const maxUploadSize = this.options.maxUploadSize
		if (!maxUploadSize || maxUploadSize <= 0 || !this.files.length) {
			return true
		}
		const tooLargeFiles = this.files.filter((file) => file.size > maxUploadSize)
		if (tooLargeFiles.length > 0) {
			void this.popups.showAlertPopup(() => (
				<Alert title="Files too Large">
					<p>The following files are too large:</p>
					<ul>
						{tooLargeFiles.map((file, i) => (
							<li key={i}>{file.name}</li>
						))}
					</ul>
					<p>Please ensure these files are less than the limit of {formatFileSize(maxUploadSize)}</p>
				</Alert>
			))
			return false
		}
		const total = this.files.map((f) => f.size).reduce((a, b) => a + b, 0)
		if (total > maxUploadSize) {
			void this.popups.showAlertPopup(() => (
				<Alert title="Files too Large">
					<p>The total size of all the files you are trying to upload is {formatFileSize(total)}.</p>
					<p>Please ensure this total is below the maximum of {formatFileSize(maxUploadSize)}</p>
				</Alert>
			))
			return false
		}
		return true
	}

	private validateMimeTypes(): boolean {
		const allowedMimeTypes = this.options.allowedMimeTypes
		if (!allowedMimeTypes || !allowedMimeTypes.length) {
			return true
		}
		const allowedLower = new Set<string>()
		for (const type of allowedMimeTypes) {
			allowedLower.add(type.trim().toLowerCase())
		}
		const invalidFiles = this.files.filter((f) => !allowedLower.has(f.type.toLowerCase()))
		if (invalidFiles.length === 0) {
			return true
		}

		void this.popups.showAlertPopup(() => (
			<Alert title="Invalid files uploaded">
				<p>The following files are not allowed to be uploaded:</p>
				<ul>
					{invalidFiles.map((file, i) => (
						<li key={i}>{file.name}</li>
					))}
				</ul>
				<p>Please remove these files and try again.</p>
			</Alert>
		))
		return false
	}
}
